import { cn } from '@/lib/utils'

interface PinDayLogoProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'full' | 'icon'
  className?: string
}

const sizes = {
  xs: { width: 24, height: 24, text: 'text-sm' },
  sm: { width: 32, height: 32, text: 'text-lg' },
  md: { width: 40, height: 40, text: 'text-xl' },
  lg: { width: 48, height: 48, text: 'text-2xl' },
  xl: { width: 64, height: 64, text: 'text-3xl' },
}

function PinDayLogoSvg({
  width,
  height,
  className,
  tone = 'default',
}: {
  width: number
  height: number
  className?: string
  tone?: 'default' | 'light'
}) {
  const isLight = tone === 'light'

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 742 1555"
      fill="none"
      width={width}
      height={height}
      role="img"
      aria-label="PinDay Logo"
      preserveAspectRatio="xMidYMid meet"
      className={cn('shrink-0 object-contain rounded-lg', className)}
    >
      <g transform="translate(-653 -267)">
        <circle cx="1023.55" cy="636.10" r="330.50" fill={isLight ? '#F08A24' : '#EB8C2B'} />
        <path
          d="M 703.00 903.00 L 703.00 1433.00 L 708.00 1453.00 L 719.00 1470.00 L 1024.00 1781.00 L 1333.00 1464.00 L 1341.00 1449.00 L 1344.00 1432.00 L 1344.00 903.00 L 1286.00 967.00 L 1249.00 997.00 L 1218.00 1017.00 L 1170.00 1041.00 L 1135.00 1053.00 L 1090.00 1064.00 L 1046.00 1069.00 L 999.00 1069.00 L 952.00 1063.00 L 906.00 1051.00 L 860.00 1033.00 L 813.00 1007.00 L 774.00 978.00 L 735.00 941.00 Z"
          fill={isLight ? '#F6EBDD' : '#263C52'}
        />
      </g>
    </svg>
  )
}

export function PinDayLogoIcon({ size = 'md', className }: Omit<PinDayLogoProps, 'variant'>) {
  const { width, height } = sizes[size]
  return <PinDayLogoSvg width={width} height={height} className={className} />
}

export function PinDayLogoIconLight({ size = 'md', className }: Omit<PinDayLogoProps, 'variant'>) {
  const { width, height } = sizes[size]
  return <PinDayLogoSvg width={width} height={height} tone="light" className={className} />
}

export function PinDayLogo({ size = 'md', variant = 'full', className }: PinDayLogoProps) {
  const { text } = sizes[size]

  if (variant === 'icon') {
    return <PinDayLogoIcon size={size} className={className} />
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <PinDayLogoIcon size={size} />
      <span className={cn('font-bold text-[var(--pd-primary)]', text)}>
        Pin<span className="text-[var(--pd-accent)]">Day</span>
      </span>
    </div>
  )
}

// Light variant for dark backgrounds (e.g., landing hero)
export function PinDayLogoLight({ size = 'md', variant = 'full', className }: PinDayLogoProps) {
  const { text } = sizes[size]

  if (variant === 'icon') {
    return <PinDayLogoIconLight size={size} className={className} />
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <PinDayLogoIconLight size={size} />
      <span className={cn('font-bold text-[#F6EBDD]', text)}>
        Pin<span className="text-[#F08A24]">Day</span>
      </span>
    </div>
  )
}
